/**
 * RES-IST CARROUSELS - Patterns B & C (Scène empilée)
 *
 * Utilisation :
 * - Initialise avec : new ResistCarousel(element)
 * - Support clavier : flèches gauche/droite
 * - Support tactile : glissement horizontal
 * - Responsive : adapte le décalage selon la largeur
 */

class ResistCarousel {
  constructor(sceneElement) {
    this.scene = sceneElement;
    this.cards = this.scene.querySelectorAll('.card');
    this.total = this.cards.length;
    this.courant = 0;
    this.depart = null;
    this.visible = true;

    if (this.total === 0) return;

    // Configuration initiale
    this.initialize();
    this.setupEventListeners();
    this.measureHeight();

    // Recalculer à chaque changement de taille
    window.addEventListener('resize', () => this.measureHeight());
    document.fonts.ready.then(() => this.measureHeight());

    // Observer l'intersection (stop si hors écran)
    new IntersectionObserver((e) => {
      this.visible = e[0].isIntersecting;
    }, { threshold: 0.25 }).observe(this.scene);

    // Mesurer la hauteur de chaque image
    this.cards.forEach((card, i) => {
      const img = card.querySelector('img');
      if (img) {
        img.addEventListener('load', () => this.measureHeight());
      }
    });
  }

  /**
   * Calcule le décalage en pourcentage selon la largeur
   */
  getOffset() {
    const width = window.innerWidth;
    if (width < 750) return 30;      // Mobile
    if (width < 1100) return 46;     // Tablet
    return 58;                       // Desktop
  }

  /**
   * Calcule la largeur de la carte en pourcentage selon la largeur
   */
  getCardWidth() {
    const width = window.innerWidth;
    if (width < 750) return 82;      // Mobile
    if (width < 1100) return 56;     // Tablet
    return 40;                       // Desktop
  }

  /**
   * Module pour boucler l'index
   */
  mod(n) {
    return ((n % this.total) + this.total) % this.total;
  }

  /**
   * Initialise les classes de la première carte
   */
  initialize() {
    this.poser(0);
  }

  /**
   * Positionne les cartes (active, prev, next)
   */
  poser(i) {
    this.courant = this.mod(i);
    const avant = this.mod(this.courant - 1);
    const apres = this.mod(this.courant + 1);

    for (let n = 0; n < this.total; n++) {
      this.cards[n].classList.remove('card--actif', 'card--prev', 'card--next');

      if (n === this.courant) {
        this.cards[n].classList.add('card--actif');
      } else if (n === avant) {
        this.cards[n].classList.add('card--prev');
      } else if (n === apres) {
        this.cards[n].classList.add('card--next');
      }

      // Mise à jour des dots
      const dot = document.querySelector(`[data-carousel-dot="${this.scene.id}"][data-index="${n}"]`);
      if (dot) {
        dot.setAttribute('aria-current', n === this.courant ? 'true' : 'false');
      }
    }
  }

  /**
   * Mesure et égalise la hauteur des cartes
   */
  measureHeight() {
    let maxHeight = 0;

    // Première passe : mesurer
    this.cards.forEach((card) => {
      card.style.height = 'auto';
      const etat = card.style.transform;
      card.style.transform = 'none';
      maxHeight = Math.max(maxHeight, card.offsetHeight);
      card.style.transform = etat;
    });

    if (maxHeight <= 0) return;

    // Fixer la hauteur du conteneur
    this.scene.style.height = maxHeight + 'px';

    // Deuxième passe : égaliser
    this.cards.forEach((card) => {
      card.style.height = maxHeight + 'px';
    });
  }

  /**
   * Lance la transition à une carte
   */
  goTo(i) {
    this.poser(i);
    // Le CSS s'occupe de l'animation via transition
  }

  /**
   * Détecte le glissement horizontal
   */
  setupEventListeners() {
    // Souris / pointeur
    this.scene.addEventListener('pointerdown', (e) => {
      this.depart = e.clientX;
    });

    this.scene.addEventListener('pointerup', (e) => {
      if (this.depart === null) return;
      const delta = e.clientX - this.depart;
      this.depart = null;

      // Minimum 40px pour considérer comme un glissement
      if (Math.abs(delta) > 40) {
        this.goTo(this.courant + (delta < 0 ? 1 : -1));
      }
    });

    this.scene.addEventListener('pointercancel', () => {
      this.depart = null;
    });

    // Clavier
    this.scene.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.goTo(this.courant - 1);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.goTo(this.courant + 1);
      }
    });

    // Navigation par boutons
    const prevBtn = this.scene.parentElement?.querySelector('[data-carousel-prev]');
    const nextBtn = this.scene.parentElement?.querySelector('[data-carousel-next]');

    if (prevBtn) prevBtn.addEventListener('click', () => this.goTo(this.courant - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => this.goTo(this.courant + 1));

    // Navigation par dots
    const dots = this.scene.parentElement?.querySelectorAll(`[data-carousel-dot="${this.scene.id}"]`);
    if (dots) {
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });
    }
  }
}

/**
 * AUTO-INITIALISATION
 * Cherche tous les éléments .scene avec l'attribut data-carousel="true"
 */

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.scene[data-carousel="true"]').forEach((scene) => {
    // Ajoute un ID unique si absent
    if (!scene.id) {
      scene.id = 'carousel-' + Math.random().toString(36).substr(2, 9);
    }
    new ResistCarousel(scene);
  });
});

/**
 * PATTERN C : Carrousels vidéo
 * Gère la lecture/pause en fonction de la visibilité
 */

class ResistCarouselVideo extends ResistCarousel {
  constructor(sceneElement) {
    super(sceneElement);
    this.videos = this.scene.querySelectorAll('video');
    this.manageVideos();

    // Observer la visibilité
    new IntersectionObserver((e) => {
      this.visible = e[0].isIntersecting;
      this.manageVideos();
    }, { threshold: 0.25 }).observe(this.scene);
  }

  /**
   * Charge et joue seulement la vidéo visible
   */
  manageVideos() {
    this.videos.forEach((video, i) => {
      if (i === this.courant && this.visible) {
        // Charger et jouer
        video.preload = 'auto';
        const p = video.play();
        if (p && p.catch) {
          p.catch(() => {
            // Autoplay refusé (par ex. iOS en économie d'énergie)
            this.showPlayButton();
          });
        }
      } else {
        // Pause et unload
        video.pause();
        video.preload = 'metadata';
      }
    });
  }

  /**
   * Lance une vidéo en cliquant sur un bouton de secours
   */
  showPlayButton() {
    // Implémentation spécifique si besoin
  }

  goTo(i) {
    super.goTo(i);
    this.manageVideos();
  }
}

// AUTO-INITIALISATION pour vidéos
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.scene[data-carousel-video="true"]').forEach((scene) => {
    if (!scene.id) {
      scene.id = 'carousel-video-' + Math.random().toString(36).substr(2, 9);
    }
    new ResistCarouselVideo(scene);
  });
});